import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import * as XLSX from "xlsx";

export default function SaveTemplate({
  showModal,
  rowsData,
  columnLabels,
  spreadsheetData,
}) {
  /*   All States
   ********************************************* */
  const dispatch = useDispatch();

  const [isSaving, setIsSaving] = useState(false);

  const [selectedWo, setSelectedWo] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(false);

  /*   All Functions
   ********************************************* */
  const loadingOn = () => {
    setLoading(true);
  };
  const loadingOff = () => {
    setLoading(false);
  };

  const createExcelWorkbook = () => {
    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Create first sheet - "Order label Sheet" with spreadsheetData and column labels
    const wsData = [
      columnLabels, // First row with column headers
      ...spreadsheetData.map(
        (row) => row.map((cell) => cell.value) // Extract just the values from each cell
      ),
    ];

    const ws1 = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws1, "Order label Sheet");

    // Create second sheet with rowsData
    const rowsDataForSheet = [
      ["rowNo", "rowType", "customHeader", "hideHeader", "column", "columns"],
      ...rowsData.map((row) => {
        const { rowNo, rowType, rowValues } = row;
        let rowData = [rowNo, rowType];

        // Add customHeader if it exists
        rowData.push(rowValues.customHeader || "");

        // Add hideHeader if it exists
        rowData.push(
          rowValues.hideHeader !== undefined ? rowValues.hideHeader : ""
        );

        // Add column if it exists
        rowData.push(rowValues.column || "");

        // Add columns as string if they exist
        rowData.push(rowValues.columns ? rowValues.columns.join(", ") : "");

        return rowData;
      }),
    ];

    const ws2 = XLSX.utils.aoa_to_sheet(rowsDataForSheet);
    XLSX.utils.book_append_sheet(wb, ws2, "Template Config");

    return wb;
  };

  const SearchWorkOrder = async () => {
    try {
      loadingOn();
      const { data } = await API.post(
        "/work-order/search",
        { searchText },
        { headers: authHeader() }
      );
      if (data?.success) {
        setSearchResults(data?.data);
        console.log("Search Work Order", data?.data);
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      loadingOff();
    }
  };

  const downloadExcel = async () => {
    try {
      setIsSaving(true);
      const wb = createExcelWorkbook();
      XLSX.writeFile(
        wb,
        `${selectedWo?.woId}-${selectedWo?.jobName}` + ".xlsx"
      );
      setIsSaving(false);
    } catch (err) {
      console.log(err.message);
      setIsSaving(false);
    }
  };

  const saveToBackend = async () => {
    if (!rowsData || !spreadsheetData || !selectedWo) {
      alert("Please select a work order and ensure data is available.");
      return;
    }
    try {
      setIsSaving(true);

      // Create workbook
      const wb = createExcelWorkbook();

      // Convert workbook to binary string
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "binary" });

      // Convert binary string to blob
      const buf = new ArrayBuffer(wbout.length);
      const view = new Uint8Array(buf);
      for (let i = 0; i < wbout.length; i++) {
        view[i] = wbout.charCodeAt(i) & 0xff;
      }
      const blob = new Blob([buf], { type: "application/octet-stream" });

      // Create form data to send to API
      const formData = new FormData();
      formData.append(
        "file",
        blob,
        `${selectedWo?.woId}-${selectedWo?.jobName}` + ".xlsx"
      );
      formData.append("woId", selectedWo?.woId);
      formData.append("folder", `kms/wo/${selectedWo?.id}/attachments`);

      const { data } = await API.post("/wo-label/template/save", formData, {
        headers: { "Content-Type": "multipart/form-data", ...authHeader() },
      });

      if (data?.success) {
        dispatch(
          setNotification({
            message: "Template saved successfully",
            type: "success",
          })
        );
        showModal(false);
      }
    } catch (error) {
      console.error("Error saving template:", error);
      alert("An error occurred while saving the template");
    } finally {
      setIsSaving(false);
    }
  };

  /*   All UseEffects
   ********************************************* */

  useEffect(() => {
    let timeout = setTimeout(() => {
      if (searchText) {
        SearchWorkOrder();
      } else {
        setSearchResults(null);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchText]);

  return (
    <div className="p-4 bg-white rounded shadow">
      <h3 className="text-xl font-bold mb-4">Save Template</h3>
      {loading && <Spinner />}

      {/* Search Work Order */}
      <div className="mb-4">
        <label
          htmlFor="searchWorkOrder"
          className="block text-sm font-medium mb-1"
        >
          Search Work Order
        </label>
        <input
          type="text"
          id="searchWorkOrder"
          className="form-control"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search job name or work order ID or customer name"
        />
        {searchResults && searchResults.length > 0 && (
          <div
            className="mt-2 bg-light py-2 px-2 rounded shadow-sm"
            style={{ maxHeight: "200px", overflowY: "auto" }}
          >
            {searchResults.map((item) => (
              <div
                key={item.id}
                className="bg-white shadow-sm custom-border-radius-sm p-1 px-2 mb-1 d-flex align-items-center justify-content-between"
              >
                <div className="w-25">{item.woId}</div>
                <div className="w-50">
                  {item?.jobName?.length > 15
                    ? `${item.jobName?.slice(0, 15)}...`
                    : item.jobName}
                </div>
                <div className="w-50">
                  {item?.customerName?.length > 13
                    ? `${item.customerName?.slice(0, 13)}...`
                    : item.customerName}
                </div>
                <button
                  onClick={() => {
                    setSelectedWo(item);
                    setSearchText(item.name);
                    setSearchResults(null);
                  }}
                  className="btn btn-sm p-0 px-1 btn-primary"
                >
                  <i className="mdi mdi-check"></i>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center mb-4">
        <p className="text-sm text-gray-600">
          This will create an Excel file with two sheets:
          <br />
          1. Order label Sheet - containing your spreadsheet data
          <br />
          2. Template Config - containing your row configuration
        </p>
      </div>

      {/*   Buttons
       ********************************************* */}
      <div className="d-flex justify-content-end gap-1">
        <button
          onClick={downloadExcel}
          className="btn btn-sm btn-primary"
          disabled={isSaving}
        >
          Download Excel
        </button>
        <button
          onClick={saveToBackend}
          className="btn btn-sm btn-success"
          disabled={
            isSaving || loading || !rowsData || !spreadsheetData || !selectedWo
          }
        >
          {isSaving ? "Saving..." : "Save to Server"}
        </button>
        <button
          onClick={() => {
            showModal(false);
          }}
          className="btn btn-sm btn-secondary"
        >
          Close
        </button>
      </div>
    </div>
  );
}
